import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "units/dashboard/context/LanguageProvider";
import Button from "../../ui/Button";
import AnimatedCounter from "./AnimatedCounter";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function HeroContent() {
  const { t } = useTranslation("landing");
  const { lang } = useLanguage();

  // برای ٪ فارسی/عربی
  const percentSuffix = lang === "en" ? "%" : "٪";

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="relative z-10 text-center lg:text-start"
    >
      {/* Badge */}
      <motion.div variants={item} className="mb-6 flex justify-center lg:justify-start">
        <span className="inline-flex items-center gap-2.5 rounded-full border border-line bg-surface px-4 py-2 text-[12.5px] font-medium text-ink-soft shadow-xs sm:text-[13px]">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-3 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-2" />
          </span>
          {t("hero.badge")}
        </span>
      </motion.div>

      {/* Title */}
      <motion.h1
        variants={item}
        className="mb-6 text-[34px] font-black leading-[1.15] tracking-tight text-ink sm:text-[44px] lg:text-[56px] xl:text-[64px]"
      >
        <span>{t("hero.title1")}</span>{" "}
        <em className="hero-underline bg-gradient-to-br from-gold to-gold-2 bg-clip-text text-transparent">
          {t("hero.titleEm")}
        </em>{" "}
        <span>{t("hero.title2")}</span>{" "}
        <span className="hero-wave">{t("hero.titleWave")}</span>
      </motion.h1>

      {/* Lead */}
      <motion.p
        variants={item}
        className="mx-auto mb-9 max-w-[560px] text-[15.5px] leading-[1.95] text-ink-soft sm:text-[17px] lg:mx-0"
      >
        {t("hero.lead")}
      </motion.p>

      {/* CTAs */}
      <motion.div
        variants={item}
        className="mb-12 flex flex-wrap justify-center gap-3 lg:justify-start"
      >
        <Button as={Link} to="/login" variant="gold" size="lg">
          {t("hero.cta1")} ✨
        </Button>
        <Button as="a" href="#demo" variant="ghost" size="lg">
          {t("hero.cta2")} ▸
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div
        variants={item}
        className="flex flex-wrap justify-center gap-x-10 gap-y-6 sm:gap-x-12 lg:justify-start"
      >
        <Stat
          to={2500}
          suffix="+"
          label={t("hero.statBusiness")}
        />
        <Stat
          to={98}
          suffix={percentSuffix}
          label={t("hero.statSatisfaction")}
        />
        <Stat
          to={24}
          suffix="/7"
          suffixEn="/7"
          suffixAr="/٧"
          label={t("hero.statSupport")}
          decimals={0}
          isSupport
        />
      </motion.div>
    </motion.div>
  );
}

function Stat({ to, suffix, suffixEn, suffixAr, label, isSupport }) {
  // برای «۲۴/۷» از data static استفاده می‌کنیم تا انیمیشن بی‌معنا نباشه
  if (isSupport) {
    return (
      <div className="text-center lg:text-start">
        <b className="block text-[28px] font-black tracking-tight text-teal-2 sm:text-[34px]">
          <StaticValue />
        </b>
        <span className="text-[13px] font-medium text-ink-soft">{label}</span>
      </div>
    );
  }

  return (
    <div className="text-center lg:text-start">
      <AnimatedCounter
        to={to}
        suffix={suffix}
        suffixEn={suffixEn}
        suffixAr={suffixAr}
        className="block text-[28px] font-black tracking-tight text-teal-2 sm:text-[34px]"
      />
      <span className="text-[13px] font-medium text-ink-soft">{label}</span>
    </div>
  );
}

function StaticValue() {
  const { lang } = useLanguage();
  if (lang === "fa") return <>۲۴/۷</>;
  if (lang === "ar") return <>٢٤/٧</>;
  return <>24/7</>;
}